<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconX } from '@tabler/icons-vue'
import type { CompanionBubble } from '@/lib/companion'
import ElfieFace from './ElfieFace.vue'

/**
 * Elfie as a companion (Dao, 1 Oct 2026): a small figure in the corner of
 * every analysis page, in place of a prompt bar across its foot, with speech
 * bubbles that take turns offering a question. The figure opens Ask Elfie;
 * a bubble opens it with its question asked. Each bubble is one the data
 * answers today, and says nothing the data does not (the caller chooses
 * them, `companionBubbles`). The reader can hide the bubbles; the figure stays.
 */
const props = defineProps<{ bubbles: CompanionBubble[] }>()
const emit = defineEmits<{ open: [question: string | null] }>()
const { t } = useI18n()

/**
 * How long each bubble stays, from the moment it shows: long enough to read
 * one with figures in it twice, and never under five seconds (Dao, 1 Oct 2026).
 */
const DWELL_MS = 8000
// Hidden for the visit, not for good: the page has no other place to bring them back from.
const HIDDEN = 'ask-elfie:bubbles-hidden'

const readHidden = (): boolean => {
  try {
    return window.sessionStorage.getItem(HIDDEN) === '1'
  } catch {
    return false
  }
}
const hidden = ref(readHidden())
const hide = (): void => {
  hidden.value = true
  try {
    window.sessionStorage.setItem(HIDDEN, '1')
  } catch {
    // Without storage the bubbles come back on the next page; nothing else depends on it.
  }
}

// Followed by key, not position: a bubble arriving as the page's data loads never cuts the one on screen short.
const shownKey = ref<string | null>(null)
const current = computed(() => props.bubbles.find((bubble) => bubble.key === shownKey.value) ?? props.bubbles[0] ?? null)
const paused = ref(false)

let timer: ReturnType<typeof setTimeout> | null = null
const stop = (): void => {
  if (timer) clearTimeout(timer)
  timer = null
}
const next = (): void => {
  timer = null
  const at = props.bubbles.findIndex((bubble) => bubble.key === current.value?.key)
  shownKey.value = props.bubbles[(at + 1) % props.bubbles.length]?.key ?? null
}
/** The bubble on screen gets its full time: when it shows, and again when the reader leaves it. */
const restart = (): void => {
  stop()
  if (!paused.value && props.bubbles.length > 1) timer = setTimeout(next, DWELL_MS)
}

watch(() => current.value?.key, restart)
watch(paused, restart)
// A second bubble arriving starts the turns; the one on screen keeps whatever time it has left.
watch(
  () => props.bubbles.length,
  (length) => {
    if (length < 2) stop()
    else if (!timer) restart()
  },
)
onMounted(restart)
onBeforeUnmount(stop)
</script>

<template>
  <div class="companion" data-test="ask-elfie-companion" @mouseenter="paused = true" @mouseleave="paused = false" @focusin="paused = true" @focusout="paused = false">
    <Transition name="bubble" mode="out-in">
      <div v-if="current && !hidden" :key="current.key" class="companion__bubble" data-test="ask-elfie-bubble">
        <button type="button" class="companion__say" aria-live="off" @click="emit('open', current.question)">{{ current.text }}</button>
        <button type="button" class="companion__hide" :aria-label="t('analyst.companion.hide')" data-test="ask-elfie-bubble-hide" @click="hide">
          <IconX :size="14" aria-hidden="true" />
        </button>
      </div>
    </Transition>

    <button type="button" class="companion__figure" :aria-label="t('analyst.name')" data-test="ask-elfie-open" @click="emit('open', null)">
      <ElfieFace />
    </button>
  </div>
</template>

<style scoped>
/* Bottom right of the page, over its content as it scrolls, never over the rail. */
.companion {
  position: fixed;
  right: var(--spacing-2xl);
  bottom: var(--spacing-2xl);
  z-index: 50;
  display: flex;
  align-items: flex-end;
  gap: var(--spacing-sm);
}

/* In Elfie's own colours, so it reads as Elfie speaking and stands out on a white page and a dark one alike. */
.companion__bubble {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xxs);
  max-width: var(--width-xs);
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-sm) var(--spacing-xs) var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, var(--companion-bubble-from), var(--companion-bubble-to));
  /* A ring in the page's own colour sets it apart from whatever it floats over, a chart included. */
  box-shadow:
    0 0 0 var(--border-width-2) var(--surface-card),
    var(--shadow-card-float);
}

/* The tail, pointing at Elfie, in the colour the gradient ends on. */
.companion__bubble::after {
  content: '';
  position: absolute;
  right: calc(var(--spacing-md) * -1 + var(--border-width-1));
  bottom: var(--spacing-md);
  border-top: var(--spacing-xs) solid transparent;
  border-bottom: var(--spacing-xs) solid transparent;
  border-left: var(--spacing-md) solid var(--companion-bubble-to);
}

.companion__say {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  font-weight: var(--font-weight-medium);
  color: var(--companion-bubble-fg);
  text-align: start;
  cursor: pointer;
}

.companion__say:hover {
  text-decoration: underline;
}

.companion__hide {
  display: inline-flex;
  flex: none;
  padding: var(--spacing-xxs);
  border: 0;
  border-radius: var(--radius-md);
  background: none;
  color: var(--companion-bubble-muted);
  cursor: pointer;
}

.companion__hide:hover {
  background: var(--companion-bubble-to);
  color: var(--companion-bubble-fg);
}

/* On the bubble, the ring in the bubble's own light colour: the page's would vanish on it. */
.companion__say:focus-visible,
.companion__hide:focus-visible {
  outline: 2px solid var(--companion-bubble-fg);
  outline-offset: 2px;
}

.companion__figure:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.companion__figure {
  display: inline-flex;
  flex: none;
  inline-size: var(--spacing-7xl);
  block-size: var(--spacing-7xl);
  padding: 0;
  border: 0;
  border-radius: var(--radius-full);
  background: none;
  box-shadow: var(--shadow-card-float);
  cursor: pointer;
  transition: transform 160ms ease;
}

.companion__figure:hover {
  transform: translateY(calc(var(--spacing-xxs) * -1));
}

.companion__figure :deep(svg) {
  inline-size: 100%;
  block-size: 100%;
}

.bubble-enter-active,
.bubble-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.bubble-enter-from,
.bubble-leave-to {
  opacity: 0;
  transform: translateY(var(--spacing-xs));
}

@media (prefers-reduced-motion: reduce) {
  .companion__figure,
  .bubble-enter-active,
  .bubble-leave-active {
    transition: none;
  }
}

@media (max-width: 767px) {
  /* Kept inside the screen's gutters, and letting taps through to the page beside the bubble. */
  .companion {
    right: var(--spacing-lg);
    bottom: var(--spacing-lg);
    left: var(--spacing-lg);
    justify-content: flex-end;
    pointer-events: none;
  }

  .companion > * {
    pointer-events: auto;
  }

  .companion__figure {
    inline-size: var(--spacing-6xl);
    block-size: var(--spacing-6xl);
  }

  .companion__bubble {
    margin-bottom: var(--spacing-md);
  }

  /* A phone has little room to spare over the page. */
  .companion__say {
    font-size: var(--font-size-body-3);
    line-height: var(--line-height-body-3);
  }
}
</style>
